import {
  Component,
  Prop,
  h,
  Host,
  State,
  Watch,
  Element,
  Listen,
  Event,
  EventEmitter,
} from '@stencil/core';
import { getDirectChildren } from '../../utils/dom';
import { debounce } from '../../utils/throttle';
import type { OptionInterface } from '../../interface';

let optIds = 0;

/**
 * Select options to be used with [nano-select](/story/nano-components-select) or [nano-datalist](/story/nano-components-select)
 *
 * @slot - main label. Defaults to `label` or `value` prop
 * @slot checked-icon - icon to indicated checked / selected state. Defaults to fontawesome light/check
 * @slot start - suitable for an icon or content displayed at the start of the main label
 * @slot end - suitable for an icon or content displayed at the end of the main label
 */
@Component({
  tag: 'nano-option',
  styleUrl: 'option.scss',
  shadow: true,
})
export class Option implements OptionInterface {
  private optId = `nano-option-${optIds++}`;
  @Element() host: HTMLNanoOptionElement;
  @State() hasFocus = false;

  constructor() {
    this.handleClick = debounce(this.handleClick.bind(this), 5);
  }

  // Public API

  /**
   * Value of the option
   */
  @Prop({ mutable: true, reflect: true }) value: string = '';

  @Watch('value')
  valueChanged() {
    if (!this.value || !this.value.length) this.value = this.labelContent;
  }

  /**
   * Label of the option
   */
  @Prop({ mutable: true, reflect: true }) label: string = '';

  @Watch('label')
  labelChanged() {
    if (!this.label || !this.label.length)
      this.label = this.labelContent.length ? this.labelContent : this.value;
  }

  /**
   * Whether this option is selected
   */
  @Prop({ reflect: true }) selected: boolean = false;

  /**
   * Whether this option should be disabled
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** You can add extra meta for this option. When displayed in a list, users
   * search / filter via extra related terms. Another usecase is 'endonyms'
   */
  @Prop() filterMeta: string = '';

  /** Fired when an option is selected */
  @Event() nanoSelect!: EventEmitter<HTMLNanoOptionElement>;

  // Event handlers

  // stop scrolling on select
  @Listen('keydown')
  handleKeyDown(e: KeyboardEvent) {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    e.preventDefault();
    this.nanoSelect.emit(this.host);
  }

  private handleClick() {
    if (this.disabled) return;
    this.nanoSelect.emit(this.host);
  }

  // Private logic

  private get labelContent() {
    return getDirectChildren(this.host, '*:not([slot])', false)
      .map((el) => el.textContent)
      .join(' ')
      .trim();
  }

  // Component lifecycle

  componentWillLoad() {
    this.valueChanged();
    this.labelChanged();
  }

  render() {
    return (
      <Host
        role="option"
        aria-selected={this.selected ? 'true' : 'false'}
        aria-disabled={this.disabled ? 'true' : 'false'}
      >
        <div
          onMouseDown={this.handleClick}
          id={this.optId}
          class={{
            option: true,
            'option--selected': this.selected,
            'option--disabled': this.disabled,
            'option--novalue': !this.value,
          }}
        >
          <div part="check-icon" class="option__check">
            <slot name="check-icon">
              <nano-icon name="light/check" aria-hidden="true" />
            </slot>
          </div>
          <div part="start" class="option__start">
            <slot name="start"></slot>
          </div>
          <div part="label" class="option__label">
            <slot>{this.label || this.value}</slot>
          </div>
          <div part="end" class="option__end">
            <slot name="end"></slot>
          </div>
        </div>
      </Host>
    );
  }
}
